import React, { memo } from 'react';
import { FiMoreVertical, FiEye, FiEyeOff } from 'react-icons/fi';
import { Tooltip } from '../../../../../components/ds';

// Shared canvas-node shell. DS treatment: NEUTRAL box (bg-2 + divider) with the
// node-classification colour carried by a left stripe + the icon + the type label
// (via the `accent` CSS-var prop from getNodeAccent). `colorClass` is a legacy
// fallback for nodes not yet migrated to `accent`.
const NodeWrapper = memo(({
    children,
    selected,
    title,
    type,
    accent,                    // e.g. 'var(--node-data)' — the classification/direction colour
    colorClass = 'text-fg-3',  // legacy fallback (un-migrated nodes)
    badge = null,
    icon: Icon,
    onMenuClick,
    className = '',
    compact = false,
    onToggleChart,
    isChartVisible = false,
    isHighlighted = false,
    style,
}) => {
    const handleMenuClick = (e) => {
        if (onMenuClick) { onMenuClick(e); return; }
        e.preventDefault();
        e.stopPropagation();
        const nodeElement = e.currentTarget.closest('.react-flow__node');
        if (nodeElement) {
            nodeElement.dispatchEvent(new MouseEvent('contextmenu', {
                bubbles: true, cancelable: true, clientX: e.clientX, clientY: e.clientY,
            }));
        }
    };

    const accentStyle = accent ? { color: accent } : undefined;

    return (
      <div style={style} className={`
          relative min-w-[200px] rounded-xl bg-bg-2 shadow-md transition-all
          ${className}
          ${selected
              ? 'ring-2 ring-[var(--color-accent)] border border-transparent'
              : isHighlighted
                  ? 'ring-2 ring-[var(--color-accent)] border border-transparent scale-[1.02]'
                  : 'border border-[var(--divider)] hover:border-[var(--border)]'
          }
      `}>
          {/* Classification / direction stripe. Clipped by a rounded overflow-hidden
              wrapper so it hugs the node's rounded corners flush (a thin stripe can't
              match a 12px radius on its own). The wrapper is pointer-events-none and
              does NOT contain the handles, so ports are never clipped. */}
          {accent && (
              <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none" aria-hidden="true">
                  <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />
              </div>
          )}

          {/* Header */}
          <div className={`
              ${compact ? 'px-3 py-1.5 pl-4' : 'px-4 py-2.5 pl-5'}
              border-b border-[var(--divider)] flex items-center justify-between
          `}>
              <div className="flex items-center gap-2 min-w-0">
                  {Icon && <Icon className={`w-3 h-3 flex-shrink-0 ${accent ? '' : colorClass}`} style={accentStyle} />}
                  <span
                      className={`text-[9px] font-bold uppercase tracking-wider truncate ${accent ? '' : colorClass.replace('bg-', 'text-')}`}
                      style={accentStyle}
                  >
                      {type}
                  </span>
                  {badge && <div className="ml-1">{badge}</div>}
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                  {onToggleChart && (
                      <Tooltip content={isChartVisible ? 'Hide from Chart' : 'Show on Chart'} placement="top">
                      <button
                          onClick={onToggleChart}
                          className={`p-0.5 rounded transition-colors ${
                              isChartVisible
                                  ? 'text-[var(--color-accent)] bg-bg-3'
                                  : 'text-fg-3 hover:text-fg-2 hover:bg-bg-3'
                          }`}
                          aria-label={isChartVisible ? 'Hide from Chart' : 'Show on Chart'}
                      >
                          {isChartVisible ? <FiEye size={12} /> : <FiEyeOff size={12} />}
                      </button>
                      </Tooltip>
                  )}
                  <Tooltip content="More Options" placement="top">
                  <button
                      className="p-0.5 hover:bg-bg-3 rounded text-fg-3 hover:text-fg-2 transition-colors"
                      onClick={handleMenuClick}
                      aria-label="More Options"
                  >
                      <FiMoreVertical size={12} />
                  </button>
                  </Tooltip>
              </div>
          </div>

          {/* Body */}
          <div className={compact ? 'p-2 pl-3' : 'p-4 pl-5'}>
              {title && <h3 className={`font-bold text-fg-1 ${compact ? 'text-[10px] mb-1' : 'text-sm mb-2'}`}>{title}</h3>}
              {children}
          </div>
      </div>
    );
});

export default NodeWrapper;
